import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { putRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { PaymentMode, AdditionalChargeType, PaymentModeScope } from 'src/lib/types/paymentSettings'
import { createChargeValidationSchema } from 'src/@core/schemas/payment-charge-schema'
import { useLocale } from 'src/lib/hooks/useLocal'
import { buildPaymentModePayload } from 'src/lib/utils/payment-page'

interface UsePaymentChargeFormProps {
  open: boolean
  selectedMode: string | null
  scope: PaymentModeScope
  onOpenChange: (open: boolean) => void
}

export const usePaymentChargeForm = ({ open, selectedMode, scope, onOpenChange }: UsePaymentChargeFormProps) => {
  const { setLoaderProperties } = useLoader()
  const {
    state: { paymentModes: contextPaymentModes },
    actions: { mutatePaymentModes }
  } = useMerchant()
  const { t } = useLocale()

  const [modesPayload, setModesPayload] = useState<PaymentMode[]>(contextPaymentModes || [])
  const [error, setError] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)

  useEffect(() => {
    if (open && contextPaymentModes) {
      setModesPayload(contextPaymentModes)
      setError('')
    }
  }, [open, contextPaymentModes])

  // A mode exists once per scope, so both have to match.
  const isTargetMode = (mode: PaymentMode) => mode.mode === selectedMode && (mode.purchase_type ?? 'CHECKOUT') === scope

  const currentMode = useMemo(() => {
    return modesPayload.find(isTargetMode)
  }, [modesPayload, selectedMode, scope])

  const additionalChargeType = currentMode?.metadata?.additionalChargeType || AdditionalChargeType.PERCENTAGE
  const additionalChargeValue = currentMode?.metadata?.additionalChargeValue || 0

  const handleChargeTypeChange = (value: string) => {
    const newType = value as AdditionalChargeType

    setModesPayload(prev =>
      prev.map(mode => {
        if (isTargetMode(mode)) {
          return {
            ...mode,
            metadata: {
              ...mode.metadata,
              additionalChargeType: newType,
              additionalChargeValue: 0
            }
          }
        }
        return mode
      })
    )
    setError('')
  }

  const handleChargeValueChange = (value: string) => {
    let numValue = parseFloat(value) || 0

    if (additionalChargeType === AdditionalChargeType.PERCENTAGE) {
      numValue = Math.min(100, Math.max(0, numValue))
      numValue = Math.round(numValue * 100) / 100
    }

    if (additionalChargeType === AdditionalChargeType.FLAT && numValue < 0) {
      setError(t('payment_additional_charges.flat_charge_must_be_greater_than_or_equal_to_zero'))
      return
    }

    setModesPayload(prev =>
      prev.map(mode => {
        if (isTargetMode(mode)) {
          return {
            ...mode,
            metadata: {
              ...mode.metadata,
              additionalChargeType: additionalChargeType,
              additionalChargeValue: numValue
            }
          }
        }
        return mode
      })
    )
    setError('')
  }

  const validate = (): boolean => {
    if (!additionalChargeType) {
      setError(t('payment_additional_charges.please_select_charge_type'))
      return false
    }

    try {
      const schema = createChargeValidationSchema(additionalChargeType)
      schema.parse({
        chargeType: additionalChargeType,
        chargeValue: additionalChargeValue || 0
      })
      setError('')
      return true
    } catch (validationError) {
      if (validationError instanceof z.ZodError) {
        const firstError = validationError.issues[0]
        setError(firstError.message)
        return false
      }
      return false
    }
  }

  const handleSave = async () => {
    if (selectedMode) {
      if (!validate() || !additionalChargeValue) {
        return
      }
    }

    setIsSaving(true)
    setLoaderProperties({ isLoaderVisible: true, message: t('payment_additional_charges.saving_additional_charges') })

    try {
      const modesApiPayload: PaymentMode[] = modesPayload.map(mode => buildPaymentModePayload(mode))

      await putRequest('/fms/v1/merchant-gateways/reorder-payment-modes', apiURI.FLO_CHECKOUT, {
        modes: modesApiPayload
      })

      mutatePaymentModes()

      successToast(t('payment_additional_charges.additional_charges_updated_successfully'))
      onOpenChange(false)
    } catch (e: unknown) {
      const error = e as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t('payment_additional_charges.failed_to_save_additional_charges')
      errorToast(errorMessage)
    } finally {
      setIsSaving(false)
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  const handleCancel = () => {
    if (contextPaymentModes) {
      setModesPayload(contextPaymentModes)
    }
    setError('')
    onOpenChange(false)
  }

  return {
    modesPayload,
    error,
    isSaving,
    additionalChargeType,
    additionalChargeValue,
    currentMode,
    handleChargeTypeChange,
    handleChargeValueChange,
    handleSave,
    handleCancel
  }
}
